import { z } from "zod";
import { Action, ActionEnum } from "notifications/notification-action";

export const notificationActionLinkContractVersion = 1 as const;

export const NotificationActionLinkV1Z = z.object({
  contractVersion: z.literal(notificationActionLinkContractVersion),
  topicCategory: z.string().min(1),
  topicId: z.uuid(),
  action: z.string().min(1).nullish(),
  /**
   * The record the topic belongs to: the work agreement or proposal a forum is the conversation of
   * (#5826). The server writes it with the notification, so it arrives whether or not the message
   * details do, and in realtime payloads too.
   */
  parent: z
    .object({
      topicCategory: z.string().min(1),
      topicId: z.uuid(),
    })
    .nullish(),
});

export type NotificationActionLinkV1 = z.infer<typeof NotificationActionLinkV1Z>;

/**
 * Actions that belong to a topic other than a proposal, and so must not fall into the proposal
 * bundle below. Naming them is what keeps `Work.Proposal` from silently accepting every action any
 * other category ever introduces.
 */
const actionsBelongingToOtherTopics: ReadonlySet<string> = new Set([
  Action.AcceptOrReject,
  Action.BuildTeamRoster,
  Action.ApproveOrDeny,
]);

const supportedActionsByTopic: Readonly<Record<string, ReadonlySet<string>>> = {
  "LegalEntities.LegalEntity": new Set(),
  "LegalEntities.EntityMember": new Set(),
  "LegalEntities.TeamRoster": new Set([Action.BuildTeamRoster]),
  // A grantor is asked to decide a colleague's access request; the requester is told the decision,
  // which asks nothing of them (#6596).
  "Permissions.AccessRequest": new Set([Action.ApproveOrDeny]),
  "Marketplace.TeamInvitation": new Set(["AcceptOrReject"]),
  "Marketplace.Team": new Set(),
  // A saved search's owner is told it has new matches; nothing is asked of them (#2411).
  "Marketplace.SavedSearch": new Set(),
  // Their profile is already hidden; publishing it again is their choice, not a request (#6608).
  "Marketplace.IndividualVisibility": new Set(),
  "Messaging.Forum": new Set(),
  "Messaging.ForumInvitation": new Set(),
  "Users.NetworkInvitation": new Set(["AcceptOrReject"]),
  "Work.Proposal": new Set(ActionEnum.filter((action) => !actionsBelongingToOtherTopics.has(action))),
  // The requester of a policy replacement is told how it went; nothing is asked of them (#6256).
  "Work.PolicyReplacement": new Set(),
  "Work.Agreement": new Set(),
};

export function parseNotificationActionLink(value: unknown): NotificationActionLinkV1 | undefined {
  const parsed = NotificationActionLinkV1Z.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

export function isKnownNotificationAction(value: string | null | undefined): value is Action {
  return value === null || value === undefined
    ? false
    : ActionEnum.includes(value as Action);
}

export function isSupportedNotificationActionLink(link: NotificationActionLinkV1): boolean {
  const supportedActions = supportedActionsByTopic[link.topicCategory];
  if (!supportedActions) return false;
  return link.action === null || link.action === undefined || supportedActions.has(link.action);
}
